import { faEyeSlash, faEye, faTrashCan, faInfoCircle, faCloudDownload } from '@fortawesome/pro-light-svg-icons'
import { QuoteOrderModelType } from '@teinor/erp/types/company/order/quote/quoteOrder'
import { QuoteOrderCommentMessageModelType, quoteOrderCommentMessageUserTypesInversed } from '@teinor/erp/types/company/order/quote/quoteOrder/quoteOrderComment/quoteOrderCommentMessage'
import { useState, useCallback, useEffect, Fragment } from 'react'
import { ButtonDisabledOrLoader } from '../../../../../../baseComponents/Button'
import { ModalMedium } from '../../../../../../baseComponents/Modal'
import { ModalEditComponentProps } from '../../../../../../baseComponents/ModalsLayouts/types'
import TwinIcon, { TwinIconToolTip } from '../../../../../../baseComponents/TwinIcon'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import FilesInput from '../../../../../../forms/FilesInput'
import FormRenderer from '../../../../../../forms/FormRenderer'
import { FormRender } from '../../../../../../forms/FormRenderer/types'
import TwinForm from '../../../../../../forms/TwinForm'
import { OnSubmit } from '../../../../../../forms/TwinForm/types'
import { twinFetchPostJSON, twinFetchPostText } from '../../../../../../utils/globals/data'
import { displayDateTime } from '../../../../../../utils/globals/date'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import useEditDeleteModal from '../../../../../../utils/hooks/useEditDeleteModal'
import useIsLoading from '../../../../../../utils/hooks/useIsLoading'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import ModalDeleteQuoteComment from './QuoteComments/ModalDeleteQuoteComment'
import ModalVisibleHideQuote from './QuoteComments/ModalVisibleHiddenComment'
import './quoteComments.sass'

interface ModalQuoteCommentsProps extends ModalEditComponentProps {
    readOnly?: boolean
}

const ModalQuoteComments: React.FC<ModalQuoteCommentsProps> = ({ setOpened, allRowData, onSubmit, readOnly }) => {
    return (
        <ModalMedium className='flex flex-col modal_comments_quote' opened={true} setOpened={setOpened}>
            <QuoteComments allRowData={allRowData?.header} onSubmit={onSubmit} readOnly={readOnly} />
        </ModalMedium>
    )
}

interface QuoteCommentsProps extends QuoteCommentsLogicProps {
    readOnly?: boolean
}

const QuoteComments: React.FC<QuoteCommentsProps> = ({ allRowData, onSubmit, readOnly }) => {
    const { t } = useTwinTranslation()
    const quoteComments = allRowData?.OrderComment?.OrderCommentMessages
    useQuoteCommentsLogic({ onSubmit, allRowData })
    const customer = t('customer', 'Cliente')
    const renderThis: JSX.Element[] = []

    if (quoteComments?.length) {
        for (const quoteComment of quoteComments) {
            renderThis.push(<QuoteComment OrderId={allRowData?.id || 0} onSubmit={onSubmit} key={'quoteComment_' + quoteComment?.id} customerName={allRowData?.Customer?.fiscal_name || allRowData?.Customer?.name || customer} {...{ quoteComment, readOnly }} />)
        }
    }

    return (
        <div className='flex-auto flex flex-col h-1'>
            <div className='flex justify-between items-center mb-20'>
                <h2>
                    <TwinTrans transKey='conversationWithCustomer'>Conversación con el cliente</TwinTrans>
                </h2>
            </div>
            <div className='flex flex-col h-1 flex-auto'>
                {!allRowData?.publicToken && !readOnly ?
                    <NotSharingMessage id={allRowData?.id || 0} />
                : null}
                <div className='flex overflow-auto flex-col quote_comments_container'>
                    {
                        renderThis.length ? renderThis : <span className='italic text-gray-9D pl-1'>{t('NoCommentsYet', 'Todavía no hay comentarios')}</span>
                    }
                </div>
                {!readOnly ? <NewCommentForm allRowData={allRowData} onSubmit={onSubmit} /> : null}
            </div>
        </div>
    )
}

interface QuoteCommentsLogicProps {
    onSubmit: OnSubmit
    allRowData: QuoteOrderModelType
}

const useQuoteCommentsLogic = ({ onSubmit, allRowData }: QuoteCommentsLogicProps) => {
    const haveToUpdateRead = allRowData?.OrderComment?.OrderCommentMessages?.length && !allRowData?.OrderComment?.OrderCommentMessages?.[0]?.readed

    const makeAllCommentsReaded = useCallback(async () => {
        if (haveToUpdateRead) {
            const res = await twinFetchPostJSON('/api/app/order/quote/comment/makeAllQuoteCommentsReaded', {
                id: allRowData?.id,
            })
            onSubmit(res, {})
        }
    }, [haveToUpdateRead, onSubmit, allRowData?.id])

    useEffect(() => {
        makeAllCommentsReaded()
    }, [makeAllCommentsReaded])

    return {}
}

interface QuoteCommentProps {
    quoteComment: QuoteOrderCommentMessageModelType
    customerName: string
    onSubmit: OnSubmit
    OrderId: number
    readOnly?: boolean
}

const QuoteComment: React.FC<QuoteCommentProps> = ({ quoteComment, customerName, onSubmit, OrderId, readOnly }) => {
    const typeManager = quoteComment?.UserType === quoteOrderCommentMessageUserTypesInversed['manager']
    return (
        <div className={(typeManager ? 'bg-gray-F7' : 'bg-green-DB') + ' p-20 rounded-xl my-10 mr-10 quote_comment' + (quoteComment?.hidden ? ' private' : '')}>
            {typeManager ? <QuoteCommentHeaderAdmin onSubmit={onSubmit} OrderId={OrderId} quoteComment={quoteComment} readOnly={readOnly} /> : <QuoteCommentHeaderCustomer author={customerName} quoteComment={quoteComment}  />}
            <div className='comment_message_content break-words whitespace-pre-wrap'>{quoteComment?.message}</div>
            {quoteComment?.file_url ?
                <CustomCommentDownloadFile id={quoteComment?.id} />
                : null}
        </div>
    )
}

interface QuoteCommentHeaderDividerProps {
    quoteComment: QuoteOrderCommentMessageModelType
}

interface QuoteCommentHeaderAdminProps extends QuoteCommentHeaderDividerProps {
    onSubmit: OnSubmit
    OrderId: number
    readOnly?: boolean
}

const QuoteCommentHeaderAdmin: React.FC<QuoteCommentHeaderAdminProps> = ({ quoteComment, onSubmit, OrderId, readOnly }) => {
    const { t } = useTwinTranslation()
    const { openModal, setOpenModal } = useEditDeleteModal()
    const hiddenIconMessage = quoteComment?.hidden ? t('makeItVisible', 'Hacer visible') : t('hide', 'Ocultar')
    let children: JSX.Element | null= null
    if (!readOnly) {
        children = (
            <Fragment>
                <div className='flex flex-row gap-x-5'>
                    <TwinIconToolTip id={'hide' + String(quoteComment?.id)} className='ml-10 cursor-pointer hover:text-green-21 text-14 hidden_admin_comment' icon={quoteComment?.hidden ? faEyeSlash : faEye} message={hiddenIconMessage} onClick={() => setOpenModal({ allRowData: { id: quoteComment?.id, OrderId }, type: 'edit' })} />
                    <TwinIconToolTip id={'delete' + String(quoteComment?.id)} className='ml-10 cursor-pointer hover:text-green-21 text-14' icon={faTrashCan} message={t('deleteComment', 'Borrar comentario')} onClick={() => setOpenModal({ allRowData: { id: quoteComment?.id, OrderId }, type: 'delete' })} />
                </div>
                {openModal?.type === 'edit' ? <ModalVisibleHideQuote isHidden={quoteComment?.hidden} allRowData={openModal.allRowData} setOpened={() => setOpenModal(null)} onSubmit={onSubmit} /> : null}
                {openModal?.type === 'delete' ? <ModalDeleteQuoteComment allRowData={openModal.allRowData} setOpened={() => setOpenModal(null)} onSubmit={onSubmit} /> : null}
            </Fragment>
        )
    }
    return (
        <QuoteCommentHeaderBase author={quoteComment.Employee?.fullname_short ?? t('responsible', 'Responsable')} quoteComment={quoteComment}>
            {children}
        </QuoteCommentHeaderBase>
    )
}

interface QuoteCommentHeaderCustomerProps extends QuoteCommentHeaderDividerProps {
    author: string
}

const QuoteCommentHeaderCustomer: React.FC<QuoteCommentHeaderCustomerProps> = ({ author, quoteComment }) => {
    return (
        <QuoteCommentHeaderBase author={author} quoteComment={quoteComment} />
    )
}


interface QuoteCommentHeaderBaseProps {
    author: string
    quoteComment: QuoteOrderCommentMessageModelType
    children?: React.ReactNode
}

const QuoteCommentHeaderBase: React.FC<QuoteCommentHeaderBaseProps> = ({ author, quoteComment, children }) => {
    let myAuthor = author
    if (quoteComment.version !== 1) {
        myAuthor = author + ' - v' + quoteComment.version
    }
    return (
        <div className='mb-6 flex gap-x-5 items-center comment_title_container'>
            <h2 className='medium12 author_comment_title'>{myAuthor}</h2>
            <div className='flex flex-auto items-center justify-between'>
                <span className='light12 date_comment_subtitle'>{displayDateTime(new Date(quoteComment?.createdAt))}</span>
                {children}
            </div>
        </div>
    )
}

interface CustomCommentDownloadFileProps extends CustomCommentDownloadFileLogicProps { }

const CustomCommentDownloadFile: React.FC<CustomCommentDownloadFileProps> = ({ id }) => {
    const { downloadFile } = useCustomCommentDownloadFileLogic({ id })
    return (
        <div className=' block text-green-21 mt-10 cursor-pointer download_file_text' onClick={(e) => downloadFile(e)}>
            <a className='hidden' download={true} href='/' target='_blank' onClick={(e) => e.stopPropagation()}>a</a>
            <TwinTrans transKey='downloadFile'>Descargar fichero</TwinTrans>
            <TwinIcon className='ml-10 ' icon={faCloudDownload} />
        </div>
    )
}

interface CustomCommentDownloadFileLogicProps {
    id: number
}

const useCustomCommentDownloadFileLogic = ({ id }: CustomCommentDownloadFileLogicProps) => {
    const downloadFile = useCallback(async (e: React.MouseEvent) => {
        const current = e.currentTarget
        const link = await twinFetchPostText('/api/app/order/quote/comment/getQuoteOrderCommentInstanceFile', { id })
        if (link && link !== 'false') {
            const a = current.querySelector('a')
            if (a) {
                a.href = link
                a.click()
            }
        }
    }, [id])

    return { downloadFile }
}

interface NewCommentFormProps extends NewCommentFormLogicProps {
    allRowData: QuoteOrderModelType
}

const NewCommentForm: React.FC<NewCommentFormProps> = ({allRowData, onSubmit}) => {
    const {t} = useTwinTranslation()
    const { handleSubmit, loading, message, onChangeMessage, startLoading, endLoading } = useNewCommentFormLogic({ onSubmit })
    const fields: FormRender<TwinDictionary> = [
        {
            cols: 1,
            className: 'mt-20',
            elements: [
                {
                    name: 'message',
                    component: 'TextArea',
                    className: 'w-full mr-15',
                    value: message,
                    onChange: (value) => onChangeMessage(value),
                    required: true,
                },
                {
                    name: 'hidden',
                    label: t('hideMessageFromCustomer', 'Ocultar mensaje para el cliente'),
                    component: 'Checkbox',
                },
                {
                    name: 'OrderId',
                    component: 'InputHidden',
                    value: allRowData?.id,
                },
                {
                    name: 'readed',
                    component: 'InputHidden',
                    value: 'true',
                }
            ]
        }
    ]

    return (
        <TwinForm action='/api/app/order/quote/comment/addQuoteOrderComment' imagepath={['quotes', String(allRowData?.id)]} beforeSubmitHandler={startLoading} onError={endLoading} onSubmit={handleSubmit}>
            <FormRenderer sections={fields} />
            <div className=' flex justify-between items-center mt-20'>
                {loading === false ? <FilesInput name='file_url' className='flex items-center' /> : null}
                <ButtonDisabledOrLoader buttonIsDisabled={message === ''} textButton={t('send', 'Enviar')} {...{ loading }} />
            </div>
        </TwinForm >
    )
}

interface NewCommentFormLogicProps {
    onSubmit: OnSubmit
}

const useNewCommentFormLogic = ({ onSubmit }: NewCommentFormLogicProps) => {
    const [message, setMessage] = useState<string>('')
    const { startLoading, endLoading, loading } = useIsLoading()
    const onChangeMessage = useCallback((value: string) => {
        setMessage(value)
    }, [setMessage])

    const handleSubmit: OnSubmit = useCallback(async (res) => {
        setMessage('')
        endLoading()
        onSubmit(res, {})

        const comments = document.querySelector('.quote_comments_container')
        if (comments) {
            comments.scrollTop = 0
        }
    }, [endLoading, onSubmit])

    return {handleSubmit, startLoading, loading, message, onChangeMessage, endLoading}
}

interface NotSharingMessageProps {
    id: number
}

const NotSharingMessage: React.FC<NotSharingMessageProps> = ({ id }) => {
    return (
        <div className='flex flex-row gap-5 items-center mb-15'>
            <TwinIconToolTip message='' className='items-center regular18' classNameIcon='text-red-BA cursor-default' icon={faInfoCircle} id={'notSharingMessage_' + id} />
            <div className='text-red-BA'>
                <TwinTrans transKey='shareQuoteOrderSoCustomerCanReadMessages'>Comparte el presupuesto para que el cliente pueda leer los mensajes</TwinTrans>
            </div>
        </div>
    )
}

export default ModalQuoteComments